import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Eye, EyeOff, ArrowLeft, Loader2, AlertCircle, Building2, Users, CheckCircle2} from 'lucide-react'
import Logo from '@/components/Logo'
import { LegalDocLink } from '@/components/LegalDocModal'
import { lumi } from '@/lib/lumi'

type AccountType = 'colaborador' | 'empresa'

function Register() {
  const navigate = useNavigate()
  const [accountType, setAccountType] = useState<AccountType>('colaborador')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPass, setConfirmPass] = useState('')
  const [otp, setOtp] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [otpSent, setOtpSent] = useState(false)
  const [error, setError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
      return () => clearTimeout(t)
    }
  }, [countdown])

  const sendOtp = async () => {
    if (!email) return setError('Por favor ingresa tu correo electrónico.')
    setError('')
    setOtpLoading(true)
    try {
      await lumi.auth.sendOtp({ email, type: 'register' })
      setCountdown(60)
      setOtpSent(true)
    } catch (e: any) {
      setError(e?.message || 'Error al enviar el código. Intenta de nuevo.')
    } finally {
      setOtpLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!otp) return setError('Por favor envía y escribe el código OTP para verificar tu correo.')
    if (password !== confirmPass) return setError('Las contraseñas no coinciden.')
    if (password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.')
    if (!acceptedTerms) return setError('Debes leer y aceptar los Términos y Condiciones para continuar.')
    setLoading(true)
    try {
      await lumi.auth.signUpWithPassword({ email, password, otp })
      // SDK auto-login tras registro; guardar tipo de cuenta en profiles
      const user = lumi.auth.user
      if (user) {
        await lumi.entities.profiles.create({
          userId: user.userId,
          accountType,
          email: user.email,
          displayName: '',
          onboardingCompleted: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      }
      // Redirigir según tipo de cuenta usando navegación SPA (sin recarga
      // completa), clave dentro del iframe del editor de Lumi para no perder
      // la sesión recién creada por el aislamiento de almacenamiento.
      if (accountType === 'empresa') {
        navigate('/empresa/perfil', { replace: true })
      } else {
        navigate('/colaborador/perfil', { replace: true })
      }
    } catch (e: any) {
      const code = e?.data?.code
      const msgs: Record<number, string> = {
        2254: 'El registro público no está habilitado en este momento.',
        2259: 'Esta cuenta no está activa.',
      }
      setError(msgs[code] || e?.message || 'Error al registrarse. Verifica los datos e intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header */}
      <header className="flex h-16 items-center justify-between px-6 lg:px-10">
        <Link to="/" aria-label="Volver al inicio">
          <Logo />
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
          <ArrowLeft size={15} /> Volver
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold text-slate-900">Crear cuenta</h1>
              <p className="mt-1.5 text-sm text-slate-500">Únete al ecosistema laboral de Beeznz Work</p>
            </div>

            {/* Tipo de cuenta */}
            <div className="mb-5 grid grid-cols-2 gap-3">
              {(
                [
                  { type: 'colaborador' as AccountType, icon: Users, label: 'Colaborador', desc: 'Busco empleo o trabajo aquí' },
                  { type: 'empresa' as AccountType, icon: Building2, label: 'Empresa', desc: 'Busco talento y contrato' },
                ] as const
              ).map(({ type, icon: Icon, label, desc }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAccountType(type)}
                  className={`relative flex flex-col items-center gap-1.5 rounded-xl border-2 p-4 text-center transition ${
                    accountType === type
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  {accountType === type && (
                    <CheckCircle2 size={14} className="absolute right-2.5 top-2.5 text-amber-500" />
                  )}
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accountType === type ? 'bg-amber-400' : 'bg-slate-100'}`}>
                    <Icon size={18} className={accountType === type ? 'text-slate-900' : 'text-slate-600'} />
                  </div>
                  <span className="text-sm font-semibold text-slate-900">{label}</span>
                  <span className="text-[11px] leading-tight text-slate-500">{desc}</span>
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email + OTP */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Correo electrónico</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    required
                    className="flex-1 rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  />
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={countdown > 0 || otpLoading}
                    className="whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                  >
                    {otpLoading ? <Loader2 size={14} className="animate-spin" /> : countdown > 0 ? `${countdown}s` : 'Enviar OTP'}
                  </button>
                </div>
                {otpSent && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 size={12} /> Código enviado. Revisa tu correo.
                  </p>
                )}
              </div>

              {/* OTP Code */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Código de verificación</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Ingresa el código de 6 dígitos"
                  className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />
              </div>

              {/* Password */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Contraseña</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    required
                    className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 pr-10 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  />
                  <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Confirmar contraseña</label>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  placeholder="Repite tu contraseña"
                  required
                  className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3.5 py-3 text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Terms checkbox */}
              <label className="flex cursor-pointer items-start gap-3">
                <div className="relative mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`h-4 w-4 rounded border-2 transition ${
                    acceptedTerms ? 'border-amber-400 bg-amber-400' : 'border-slate-300 bg-white'
                  } flex items-center justify-center`}>
                    {acceptedTerms && <CheckCircle2 size={11} className="text-white" />}
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-slate-500">
                  He leído y acepto los{' '}
                  <LegalDocLink docKey="termsAndConditions">Términos y Condiciones</LegalDocLink>
                  {', '}
                  el{' '}
                  <LegalDocLink docKey="privacyPolicy">Aviso de Privacidad</LegalDocLink>
                  {' y la '}
                  <LegalDocLink docKey="evaluationsPolicy">Política de Evaluaciones y Reputación</LegalDocLink>
                  {' de Beeznz Work.'}
                </p>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? 'Creando cuenta...' : `Crear cuenta como ${accountType === 'empresa' ? 'Empresa' : 'Colaborador'}`}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-slate-500">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="font-semibold text-amber-600 hover:underline">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

export default Register
